import { useCallback, useEffect, useState } from 'react';
import * as engine from './engine/index.js';
import AccessGate from './components/AccessGate.jsx';
import AppHeader from './components/AppHeader.jsx';
import ReviewForm from './components/ReviewForm.jsx';
import ResultsArea from './components/ResultsArea.jsx';
import CostDashboard from './components/CostDashboard.jsx';
import LeadsPage from './components/LeadsPage.jsx';
import { useEngineHandlers } from './hooks/useReview.js';
import { persistReview } from './lib/reviewApi.js';
import { isTrial } from './lib/session.js';
import { spOpenAdmin } from './lib/accessGate.js';

/* The shell.
 *
 * React owns the chrome; the engine owns the analysis and, for now, the report
 * markup. The two meet at element ids and class names, both lifted from the
 * single-file build: the engine looks up #lob and #uploadZone, writes the file
 * list into #fileList and the report into #fullReview, and the stylesheet in
 * src/styles.css is the original one. That is why none of it is renamed.
 *
 * The export buttons, the tab switcher and the review lifecycle all live in
 * the engine already and are bound by id, so this component wires them rather
 * than reimplementing them.
 */
export default function App() {
  // Bind everything the single-file build attached at parse time — the form,
  // the dropzone, the toolbar — now that the markup is mounted.
  useEngineHandlers(true);

  useEffect(() => {
    // Buttons the engine owns but never bound itself, because index.html had
    // them as inline onclick attributes. Bind by id so the handlers stay in
    // the engine and this stays a wiring layer.
    const wire = (id, fn) => {
      const el = document.getElementById(id);
      if (!el || el._spWired) return;
      el._spWired = 1;
      el.addEventListener('click', (ev) => {
        const f = typeof fn === 'string' ? window[fn] : fn;
        if (typeof f === 'function') f(ev);
      });
    };
    // Taken verbatim from the inline onclick attributes the single-file build
    // used, so the names are the engine's own — spOpenEoRecord, not a guess at
    // openRecordModal. The attributes themselves are gone: React ignores
    // lowercase onclick, so leaving them in reads as wiring that does nothing.
    wire('expandAllBtn', 'spToggleAllDetails');
    wire('printBtn', 'handlePrint');
    wire('compactPdfBtn', () => window.handleCompactPDF && window.handleCompactPDF());
    wire('demoPdfBtn', 'handleDemoPrint');
    wire('demoCompactPdfBtn', () => window.handleCompactPDF && window.handleCompactPDF(true));
    wire('eoRecordBtn', 'spOpenEoRecord');
    wire('declinationBtn', 'spOpenDeclinationForm');
    wire('spDemoBtn', 'spToggleDemo');
    wire('newReviewBtn', 'startNewReview');

    // The tab strip: the engine's switchTab does the work.
    document.querySelectorAll('.tab-btn').forEach((btn) => {
      if (btn._spWired) return;
      btn._spWired = 1;
      const tab = btn.getAttribute('data-tab');
      btn.addEventListener('click', () => window.switchTab && window.switchTab(tab));
    });
  }, []);

  const [reviewId, setReviewId] = useState(null);
  const [persistError, setPersistError] = useState(null);

  // An administrator gets the application, the cost dashboard and the
  // free-trial leads as tabs in the masthead. accessGate.js says when that
  // starts and ends.
  const [admin, setAdmin] = useState(false);
  const [tab, setTab] = useState('app');
  useEffect(() => {
    const onView = (e) => { setAdmin(Boolean(e.detail && e.detail.on)); setTab('app'); };
    window.addEventListener('sp:admin-view', onView);
    return () => window.removeEventListener('sp:admin-view', onView);
  }, []);
  const showCosts = admin && tab === 'costs';
  const showLeads = admin && tab === 'leads';

  const onSubmit = useCallback(async (e) => {
    const fn = window.onAnalysisSubmit || engine.onAnalysisSubmit;
    if (typeof fn !== 'function') { e.preventDefault(); return; }

    // The engine renders the report as it always has. Persistence happens
    // after, from what it left on the window, and never blocks it: the broker
    // is already reading the analysis, and losing the record is not a reason
    // to lose the review.
    await fn(e);
    setPersistError(null);

    // The full review path puts the extracted text on _lastFormData; the
    // quick screen sets pdfTexts to [] because it does not read the documents.
    // Nothing to store in that case, and storing an empty review would be
    // worse than storing none.
    const form = window._lastFormData;
    const pdfTexts = (form && form.pdfTexts) || [];
    if (!pdfTexts.length) return;
    // A public trial's analysis is not recorded; the server refuses it too.
    if (isTrial()) return;

    const out = await persistReview({ form, pdfTexts });
    if (out.ok) {
      setReviewId(out.id);
      window._spReviewId = out.id;
    } else if (!out.skipped) {
      setPersistError(out.error);
    }
  }, []);

  return (
    <AccessGate>
      <AppHeader>
        {admin ? <AdminTabs tab={tab} onTab={setTab} /> : null}
      </AppHeader>
      {/* Hidden, not unmounted: the engine keeps its review in this DOM, so a
          look at the costs must not lose the report being read. */}
      <div id="spAppView" role={admin ? 'tabpanel' : undefined} aria-labelledby={admin ? 'spTabApp' : undefined} hidden={showCosts || showLeads}>
        <ReviewForm onSubmit={onSubmit} />
        <ResultsArea />
        <ReviewRecord id={reviewId} error={persistError} />
      </div>
      {admin ? <CostDashboard active={showCosts} /> : null}
      {admin ? <LeadsPage active={showLeads} /> : null}
    </AccessGate>
  );
}

/* The administrator's tabs. The demo-link manager is a dialog, not a tab: it
 * is a form used now and then, and opening it leaves the current tab as it is. */
function AdminTabs({ tab, onTab }) {
  const item = (key, id, label) => (
    <button type="button" role="tab" id={id} className="sp-admin-tab"
      aria-selected={tab === key} aria-controls={{ app: 'spAppView', costs: 'spCostPage', leads: 'spLeadsPage' }[key]}
      onClick={() => { onTab(key); window.scrollTo(0, 0); }}>{label}</button>
  );
  return (
    <nav className="sp-admin-tabs" aria-label="Administrator">
      <div role="tablist" className="sp-admin-tabs">
        {item('app', 'spTabApp', 'Application')}
        {item('costs', 'spTabCosts', 'Cost Observability')}
        {item('leads', 'spTabLeads', 'Leads')}
      </div>
      <button type="button" className="sp-admin-tab sp-admin-tab--aside" onClick={spOpenAdmin}>Demo Links</button>
    </nav>
  );
}

/* Where the review was stored, or why it was not.
 *
 * Small and out of the way on purpose. It is a receipt, not a feature: the
 * report above it is the work. But a silent failure to persist would mean a
 * broker believing a review is saved when it is not, so a failure says so.
 */
function ReviewRecord({ id, error }) {
  if (!id && !error) return null;
  return (
    <div className="data-privacy-notice" style={{ margin: '12px 16px' }}>
      {id
        ? <>Recorded as review <code>{id}</code>. The findings are kept; the
            policy documents and this report are <strong>not</strong> stored —
            export it now if you need to keep it.</>
        : <>This analysis was <strong>not recorded</strong>: {error}. The
            report above is complete and can still be exported.</>}
    </div>
  );
}
